import mapboxgl from 'mapbox-gl';
import VehicleMarker from '../components/VehicleMarker/VehicleMarker';
import { createRoot } from 'react-dom/client';
import { Subject } from 'rxjs';
import { Logic } from './logic';

export interface VehicleModel {
    address: string;
    coordinate: {
        lon: number;
        lat: number;
    };
    headingAngle: number;
    updatedOn: string;
}

export class VehicleLogic {
    private _marker?: mapboxgl.Marker;

    vehicleUpdated$: Subject<VehicleModel>;

    constructor(private _logic: Logic, private _map?: mapboxgl.Map) {
        this.vehicleUpdated$ = new Subject<VehicleModel>();
    }

    update(vehicle: VehicleModel, alignLeft?: number): void {
        if (this._marker && vehicle.coordinate.lat && vehicle.coordinate.lon) {
            this._marker.setLngLat({
                lat: vehicle.coordinate.lat,
                lng: vehicle.coordinate.lon
            });

            this.vehicleUpdated$.next(vehicle);
        } else {
            this._marker = this._createVehicleMarker(vehicle);

            if (this._map) {
                this._marker?.addTo(this._map);
            }
        }

        this.fitVehicle(alignLeft);
    }

    remove() {
        if (this._marker) {
            this._marker.remove();
            this._marker = undefined;
        }
    }

    fitVehicle(alignLeft?: number) {
        if (this._marker) {
            const lngLat = this._marker.getLngLat();
            const bounds = this._getCenterBoundsWithDistance(lngLat.lng, lngLat.lat, 2000);
            this._map?.fitBounds(bounds, { offset: [alignLeft || 0, 0] });
        }
    }

    private _getCenterBoundsWithDistance(lng: number, lat: number, distanceInMeters = 2000): mapboxgl.LngLatBounds {
        const center = new mapboxgl.LngLat(lng, lat);
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend(center.toBounds(distanceInMeters));
        return bounds;
    }

    private _createVehicleMarker(vehicle: VehicleModel): mapboxgl.Marker {
        const vehicleMarkerElement = document.createElement('div');
        vehicleMarkerElement.id = 'vehicle';

        const root = createRoot(vehicleMarkerElement);
        root.render(<VehicleMarker vehicle={vehicle} logic={this._logic} />);

        const marker = new mapboxgl.Marker({ element: vehicleMarkerElement });
        marker.setLngLat([vehicle.coordinate.lon, vehicle.coordinate.lat]);

        return marker;
    }
}
